/* eslint-disable react/jsx-props-no-spreading */
import {
  Avatar,
  Combobox,
  ComboboxItem,
  Group,
  InputBase,
  Stack,
  Text,
  useCombobox
} from "@mantine/core";
import { useState } from "react";

// Define proper type for a user
type User = {
  value: string;
  name?: string;
  email?: string;
  avatar_url?: string;
};

type Props = {
  users?: User[],
  onUserSelect?: (user: User | null) => Promise<void>;
  value?: User | null,
  placeHolder?: string,
  disabled?: boolean,
};

interface ItemProps extends ComboboxItem {
  avatar_url?: string;
  email?: string;
  name?: string; // Make name optional to match User type
  label: string; // Must be required as ComboboxItem requires it
  user?: User; // Store the full user object
}

// Option component - displayed in the dropdown
function SelectOption({ avatar_url, email, name = "", ...others }: ItemProps) {
  return (
    <Combobox.Option {...others}>
      <Group wrap="nowrap" gap="xs">
        <Avatar name={name} variant="filled" src={avatar_url} color="initials" radius="xl" size="sm" alt={name}>
          {name ? name[0].toUpperCase() : ""}
        </Avatar>
       <Stack gap={0}>
          <Text size="12px" truncate className="!leading-1/4">
            {name}
          </Text>
          <Text size="10px" opacity={0.65} truncate className="!leading-none italic">
            {email}
          </Text>
        </Stack>
      </Group>
    </Combobox.Option>
  );
}

// No longer need the SelectValue component as we're using leftSection

const AddUserDropdown = ({
  value: externalValue, users = [], onUserSelect = () => Promise.resolve(), placeHolder, disabled = false,
}: Props) => {
  // Local state for selected user
  const [selectedUser, setSelectedUser] = useState<User | null>(externalValue || null);
  
  // Initialize search with the selected user's name
  const [search, setSearch] = useState(externalValue?.name || '');
  
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const displayName = selectedUser?.name || '';

  // Filter users based on search text
  const filteredUsers = search.trim() !== ''
    ? users.filter(user => 
        user.name?.toLowerCase().includes(search.toLowerCase().trim()) || 
        user.email?.toLowerCase().includes(search.toLowerCase().trim())
      )
    : users;

  // Transform filtered users array into Combobox options
  const items = filteredUsers.map((user) => (
    <SelectOption
      key={user.value}
      value={user.value}
      avatar_url={user.avatar_url}
      email={user.email}
      name={user.name || ""}
      label={user.name || ""}
      user={user} // Pass the full user object
    />
  ));

  return (
    <Combobox 
      store={combobox}
      withinPortal={false}
      disabled={disabled}
      onOptionSubmit={(val: string) => {
        const foundUser = users.find(user => user.value === val);
        if (foundUser) {
          setSelectedUser(foundUser);
          setSearch(foundUser.name || '');
          onUserSelect(foundUser);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          rightSection={<Combobox.Chevron />}
          value={search}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch(selectedUser ? displayName : '');
          }}
          placeholder={placeHolder || "Select user..."}
          rightSectionPointerEvents="none"
          size="xs"
          ff="Nunito"
          classNames={{
            input: "pl-8 text-gray-600 rounded-md border border-gray-300 font-nunito text-sm",
            section: "bg-gray-100",
          }}
          disabled={disabled}
          leftSection={
            <Avatar 
              color="initials"
              src={selectedUser?.avatar_url} 
              radius="xl" 
              name={displayName}
              variant="filled"
              size={20} 
              alt={displayName || "HyperEngage"}
            >
              {displayName ? displayName[0].toUpperCase() : ""}
            </Avatar>
          }
        />
      </Combobox.Target>

      <Combobox.Dropdown mah={400} className="overflow-y-auto">
        <Combobox.Options>
          {items.length > 0 ? items : <Combobox.Empty>Nobody here</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default AddUserDropdown;
